// Text templates: list, create, edit (name/body), delete with an in-page confirm. Scope is chosen at
// create time and then fixed: staff may make Personal or ProZpects templates, a partner's side
// Personal or Team ones (the API 422s any other scope).
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useCrm } from '../../lib/crm';
import { SCOPE_LABELS } from './TemplatePicker';

export default function TemplatesManager({ onClose }) {
  const { user } = useAuth();
  const crm = useCrm();
  const scopes = user?.role === 'partner' ? ['personal', 'partner'] : ['personal', 'prozpects'];
  const [templates, setTemplates] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [editing, setEditing] = useState(null); // null | 'new' | template id
  const [form, setForm] = useState({ name: '', body: '', scope: 'personal' });
  const [confirming, setConfirming] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    crm.listTemplates()
      .then((t) => { if (live) setTemplates(t); })
      .catch(() => { if (live) setError('Could not load templates.'); })
      .finally(() => { if (live) setLoaded(true); });
    return () => { live = false; };
  }, [crm]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const startNew = () => { setEditing('new'); setForm({ name: '', body: '', scope: 'personal' }); setError(''); };
  const startEdit = (t) => { setEditing(t.id); setForm({ name: t.name, body: t.body, scope: t.scope }); setError(''); };

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true); setError('');
    try {
      if (editing === 'new') {
        const t = await crm.createTemplate({ name: form.name.trim(), body: form.body.trim(), scope: form.scope });
        setTemplates((l) => [...l, t]);
      } else {
        const t = await crm.patchTemplate(editing, { name: form.name.trim(), body: form.body.trim() });
        setTemplates((l) => l.map((x) => (x.id === t.id ? t : x)));
      }
      setEditing(null);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Could not save that template.');
    }
    setSaving(false);
  };

  const remove = async (id) => {
    setError('');
    try {
      await crm.deleteTemplate(id);
      setTemplates((l) => l.filter((x) => x.id !== id));
      setConfirming(null);
    } catch {
      setError('Could not delete that template.');
    }
  };

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="templates-title" onClick={onClose}>
      <div className="modal-panel cr-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-close" onClick={onClose} aria-label="Close">&times;</button>
        <h2 id="templates-title" className="cr-modal__title">Text templates</h2>
        {editing == null && (
          <div className="cr-actions">
            <button type="button" className="adm-btn adm-btn--primary adm-btn--sm" onClick={startNew} data-testid="template_new">+ New template</button>
          </div>
        )}
        {editing != null && (
          <form onSubmit={save}>
            <div className="cr-form">
              <label className="adm-field cr-form__wide"><span className="adm-field__label">Name</span>
                <input className="adm-input" value={form.name} onChange={set('name')} data-testid="template_name" required /></label>
              <label className="adm-field cr-form__wide"><span className="adm-field__label">Text</span>
                <textarea className="adm-input cr-textarea" value={form.body} onChange={set('body')} data-testid="template_body" required /></label>
              {editing === 'new' && (
                <label className="adm-field"><span className="adm-field__label">Who can use it</span>
                  <select className="adm-select" value={form.scope} onChange={set('scope')} data-testid="template_scope">
                    {scopes.map((s) => <option key={s} value={s}>{SCOPE_LABELS[s]}</option>)}
                  </select></label>
              )}
              <p className="cr-muted cr-form__wide">Merge fields: {'{first_name}'} {'{last_name}'} {'{school}'} {'{sender_first_name}'}</p>
            </div>
            <div className="cr-modal__actions">
              <button type="button" className="adm-btn" onClick={() => setEditing(null)} data-testid="template_cancel">Cancel</button>
              <button type="submit" className="adm-btn adm-btn--primary" disabled={saving || !form.name.trim() || !form.body.trim()} data-testid="template_save">
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        )}
        {error && <div role="alert" className="cr-error">{error}</div>}
        <ul className="cr-list" data-testid="templates_list">
          {templates.map((t) => (
            <li key={t.id} className="cr-activity" data-testid={`template_row_${t.id}`}>
              <div><strong>{t.name}</strong> <span className="adm-chip">{SCOPE_LABELS[t.scope] || t.scope}</span></div>
              <div className="cr-notes">{t.body}</div>
              {t.mine && (
                confirming === t.id ? (
                  <div className="cr-actions">
                    <span>Delete this template?</span>
                    <button type="button" className="adm-btn adm-btn--sm adm-btn--primary" onClick={() => remove(t.id)} data-testid={`template_delete_confirm_${t.id}`}>Delete</button>
                    <button type="button" className="adm-btn adm-btn--sm" onClick={() => setConfirming(null)}>Keep</button>
                  </div>
                ) : (
                  <div className="cr-actions">
                    <button type="button" className="adm-btn adm-btn--sm" onClick={() => startEdit(t)} data-testid={`template_edit_${t.id}`}>Edit</button>
                    <button type="button" className="adm-btn adm-btn--sm" onClick={() => setConfirming(t.id)} data-testid={`template_delete_${t.id}`}>Delete</button>
                  </div>
                )
              )}
            </li>
          ))}
        </ul>
        {loaded && templates.length === 0 && <p className="cr-muted">No templates yet.</p>}
      </div>
    </div>
  );
}
